import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Bulk Artist Import flow:
 * picker -> search -> confirm -> importing -> done
 *
 * Used both inside the wizard (step 2) and from Settings.
 */
export default function ClaimArtistFlow({ defaultName = "", onClose, onDone, embedded = false }) {
  const [stage, setStage] = useState("picker");
  const [platform, setPlatform] = useState(null);
  const [query, setQuery] = useState(defaultName);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const pickPlatform = async (p) => {
    setPlatform(p);
    setStage("search");
    setResults([]);
    if (defaultName) {
      // Auto-search
      setTimeout(() => doSearch(p, defaultName), 50);
    }
  };

  const doSearch = async (p = platform, q = query) => {
    if (!q.trim()) return;
    setLoadingSearch(true);
    try {
      const r = await api.post("/connections/search", { platform: p, query: q.trim() });
      setResults(r.data);
      if (r.data.length === 0) toast("No matches — try a different name");
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
        onClose?.();
      } else {
        toast.error(typeof det === "string" ? det : "Search failed");
      }
    } finally {
      setLoadingSearch(false);
    }
  };

  const startImport = async () => {
    if (!selected) return;
    try {
      const r = await api.post("/connections/claim", {
        platform,
        platform_artist_id: selected.id,
        platform_artist_name: selected.name,
        artwork_url: selected.artwork || "",
      });
      setJob({ id: r.data.import_job_id, total_count: 0, progress_count: 0, current_track_name: "", status: "pending" });
      setStage("importing");
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/import-jobs/${r.data.import_job_id}`);
          setJob(j.data);
          if (j.data.status === "done" || j.data.status === "failed") {
            clearInterval(pollRef.current);
            if (j.data.status === "done") setStage("done");
            else toast.error(j.data.error_message || "Import failed");
          }
        } catch { /* keep polling */ }
      }, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Could not start import");
    }
  };

  const finish = () => {
    onDone?.(job);
  };

  return (
    <div className={embedded ? "" : "fade-in"}>
      {stage === "picker" && (
        <Picker onPick={pickPlatform} onSkip={onClose} />
      )}
      {stage === "search" && (
        <Search
          platform={platform}
          query={query}
          setQuery={setQuery}
          results={results}
          loading={loadingSearch}
          selected={selected}
          setSelected={setSelected}
          onSearch={() => doSearch()}
          onConfirm={() => setStage("confirm")}
          onBack={() => setStage("picker")}
        />
      )}
      {stage === "confirm" && (
        <Confirm
          platform={platform}
          selected={selected}
          onBack={() => setStage("search")}
          onConfirm={startImport}
        />
      )}
      {stage === "importing" && (
        <Importing platform={platform} job={job} />
      )}
      {stage === "done" && (
        <Done platform={platform} job={job} onContinue={finish} />
      )}
    </div>
  );
}

function Picker({ onPick, onSkip }) {
  return (
    <div>
      <h2 className="serif" style={{ fontSize: 36, lineHeight: 1.05, margin: "0 0 8px" }} data-testid="claim-picker-title">
        Have an existing catalog?
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14.5, lineHeight: 1.6 }}>
        Connect your artist profile from a streaming service to import your full discography in one shot — every track auto-populates with title, ISRC, duration, release date, album, and artwork.
      </p>
      <div className="claim-services">
        <button className="claim-service" onClick={() => onPick("apple")} data-testid="pick-apple-btn">
          <div className="claim-service-icon apple">♪</div>
          <div className="claim-service-name">Apple Music</div>
          <div className="claim-service-desc">Pulls full metadata including ISRC and release dates from Apple's catalog.</div>
        </button>
      </div>
      {onSkip && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onSkip} data-testid="skip-claim-btn">
            Skip — start with empty catalog →
          </button>
        </div>
      )}
    </div>
  );
}

function Search({ platform, query, setQuery, results, loading, selected, setSelected, onSearch, onConfirm, onBack }) {
  return (
    <div>
      <button className="btn btn-ghost" onClick={onBack} style={{ paddingLeft: 0, marginBottom: 8 }}>
        ← Back
      </button>
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Apple Music</div>
      <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: "8px 0 6px" }}>
        Find your artist profile
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14, marginBottom: 14 }}>
        We'll search Apple Music and pull in the full discography.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <input
          data-testid="claim-search-input"
          className="lq-input"
          placeholder="Search artist name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
        />
        <button className="btn btn-light" onClick={onSearch} disabled={loading} data-testid="claim-search-btn">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      <div className="slide-up-stagger">
        {results.length === 0 && !loading && (
          <div className="callout">No artists yet — try a search.</div>
        )}
        {results.map((r) => (
          <div
            key={r.id}
            className={`claim-result ${selected?.id === r.id ? "selected" : ""}`}
            onClick={() => setSelected(r)}
            data-testid={`claim-result-${r.id}`}
          >
            <div className="claim-result-art" style={r.artwork ? { backgroundImage: `url(${r.artwork})` } : undefined}>
              {!r.artwork && r.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="claim-result-name">{r.name}</div>
              <div className="claim-result-meta">
                {r.followers ? `${formatNum(r.followers)} followers · ` : ""}
                {(r.genres || []).slice(0, 2).join(" · ") || r.primary_genre || ""}
              </div>
            </div>
            {selected?.id === r.id && <span style={{ color: "var(--accent)", fontFamily: "var(--mono)", fontSize: 11 }}>✓</span>}
          </div>
        ))}
      </div>

      {selected && (
        <button className="btn btn-primary btn-block" onClick={onConfirm} style={{ marginTop: 16 }} data-testid="claim-continue-btn">
          Continue with {selected.name} →
        </button>
      )}
    </div>
  );
}

function Confirm({ platform, selected, onBack, onConfirm }) {
  return (
    <div>
      <button className="btn btn-ghost" onClick={onBack} style={{ paddingLeft: 0, marginBottom: 8 }}>
        ← Back to results
      </button>
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Confirm Import</div>
      <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: "8px 0 16px" }}>
        Ready to import?
      </h2>

      <div style={{ background: "var(--bg-elev-2)", padding: 18, borderRadius: 6, border: "1px solid var(--border-soft)" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 14 }}>
          <div className="claim-result-art" style={selected.artwork ? { backgroundImage: `url(${selected.artwork})`, backgroundSize: "cover" } : undefined}>
            {!selected.artwork && selected.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 18, color: "var(--text)" }}>{selected.name}</div>
            <div className="data-line">from Apple Music</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-mute)", lineHeight: 1.6, margin: 0 }}>
          Existing tracks (matched by ISRC) will be updated with the latest metadata, not duplicated. You can always remove tracks afterward.
        </p>
      </div>

      <button className="btn btn-primary btn-block" onClick={onConfirm} style={{ marginTop: 18 }} data-testid="confirm-import-tracks-btn">
        Import all tracks →
      </button>
    </div>
  );
}

function Importing({ platform, job }) {
  const total = job?.total_count || 0;
  const progress = job?.progress_count || 0;
  const pct = total ? Math.round((progress / total) * 100) : 0;
  return (
    <div>
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Importing</div>
      <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: "8px 0 4px" }}>
        Pulling your catalog…
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14, marginBottom: 22 }}>
        Backend is fetching each track's metadata from Apple Music — title, ISRC, duration, release date, album, artwork.
      </p>

      <div className="progress-card" data-testid="import-progress-card">
        <div className="progress-counter" data-testid="import-progress-num">{progress}</div>
        <div className="progress-of">of {total || "…"} imported</div>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
        <div className="progress-now">Now importing: <span>{job?.current_track_name || "…"}</span></div>
      </div>
    </div>
  );
}

function Done({ platform, job, onContinue }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{
        width: 56, height: 56, borderRadius: "50%",
        background: "rgba(21, 128, 61, 0.18)", color: "var(--green-light)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 28, marginBottom: 16,
      }}>✓</div>
      <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: "0 0 8px" }} data-testid="claim-done-title">
        Library imported
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14 }}>
        <strong style={{ color: "var(--text)" }}>{job?.progress_count || 0} tracks</strong> from Apple Music are now in your catalog with full metadata.
      </p>
      <button className="btn btn-primary" onClick={onContinue} style={{ marginTop: 20 }} data-testid="claim-done-continue-btn">
        Continue →
      </button>
    </div>
  );
}

function formatNum(n) {
  if (!n) return "0";
  if (n > 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n > 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
